import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { probeGmailAccess } from "@/services/gmailService";
import {
  GOOGLE_CONFIG,
  GOOGLE_DRIVE_SCOPES,
  clearGoogleOAuthPending,
  consumeGoogleOAuthRedirect,
  fetchTokenInfo,
  fetchUserProfile,
  getStoredProfile,
  getStoredToken,
  gmailConnectOptions,
  googleOAuthReturnPath,
  loadGoogleScripts,
  restoreSession,
  revokeAndClear,
  saveToken,
  startGoogleOAuthRedirect,
  tokenHasDriveScopes,
  tokenHasGmailScope,
  validateToken,
  GoogleAuthError,
  type GoogleUserProfile,
} from "@/lib/googleAuth";

export type GoogleConnection = {
  /** Any valid Google token in storage */
  connected: boolean;
  /** Token includes Drive scopes */
  driveConnected: boolean;
  /** Token includes gmail.readonly */
  gmailConnected: boolean;
  token: string | null;
  profile: GoogleUserProfile | null;
  isLoading: boolean;
  error: string | null;
  scriptsLoaded: boolean;
};

/** @deprecated Use GoogleConnection */
export type GoogleDriveConnection = GoogleConnection;

type GoogleConnectionContextValue = {
  connection: GoogleConnection;
  connectDrive: () => void;
  connectGmail: () => void;
  /** @deprecated Use connectDrive or connectGmail */
  connect: () => void;
  disconnect: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  applySession: (token: string, profile?: GoogleUserProfile | null) => void | Promise<void>;
  handleAuthFailure: () => void;
  /** Clears Gmail connection only; preserves Drive session when possible */
  handleGmailAuthFailure: () => void;
  config: typeof GOOGLE_CONFIG;
};

const GoogleConnectionContext =
  createContext<GoogleConnectionContextValue | null>(null);

async function readScopeFlags(token: string | null): Promise<{
  driveConnected: boolean;
  gmailConnected: boolean;
}> {
  if (!token) return { driveConnected: false, gmailConnected: false };
  const info = await fetchTokenInfo(token);
  return {
    driveConnected: tokenHasDriveScopes(info),
    gmailConnected: tokenHasGmailScope(info),
  };
}

export function GoogleConnectionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<GoogleUserProfile | null>(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);

  const invalidateGoogleQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["google-drive"] });
    queryClient.invalidateQueries({ queryKey: ["gmail-messages"] });
    queryClient.invalidateQueries({ queryKey: ["gmail-message"] });
  }, [queryClient]);

  const syncScopeFlags = useCallback(async (nextToken: string | null) => {
    const flags = await readScopeFlags(nextToken);
    setDriveConnected(flags.driveConnected);
    setGmailConnected(flags.gmailConnected);
    return flags;
  }, []);

  const applySession = useCallback(
    async (nextToken: string, nextProfile?: GoogleUserProfile | null) => {
      setToken(nextToken);
      setProfile(nextProfile ?? getStoredProfile());
      setError(null);
      await syncScopeFlags(nextToken);
      invalidateGoogleQueries();
    },
    [syncScopeFlags, invalidateGoogleQueries]
  );

  const handleAuthFailure = useCallback(() => {
    setToken(null);
    setProfile(null);
    setDriveConnected(false);
    setGmailConnected(false);
    setError("Google session expired. Please sign in again.");
    invalidateGoogleQueries();
  }, [invalidateGoogleQueries]);

  const handleGmailAuthFailure = useCallback(() => {
    setGmailConnected(false);
    setError("Gmail access expired. Please sign in again.");
    invalidateGoogleQueries();
  }, [invalidateGoogleQueries]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const parsed = consumeGoogleOAuthRedirect();
      if (!parsed) return;
      if (!parsed.ok) {
        setError(parsed.error);
        clearGoogleOAuthPending();
        return;
      }
      try {
        const profile = await fetchUserProfile(parsed.accessToken);
        saveToken(parsed.accessToken, profile);
        const info = await fetchTokenInfo(parsed.accessToken);
        if (tokenHasGmailScope(info)) {
          try {
            await probeGmailAccess(parsed.accessToken);
          } catch {
            /* Inbox pages surface Gmail API errors after return. */
          }
        }
        if (cancelled) return;
        await applySession(parsed.accessToken, profile);
        clearGoogleOAuthPending();
        const ret = googleOAuthReturnPath();
        const here = `${window.location.pathname}${window.location.search}`;
        if (here !== ret) {
          window.location.replace(ret);
        }
      } catch (e) {
        if (!cancelled) {
          clearGoogleOAuthPending();
          setError(e instanceof Error ? e.message : "Google sign-in failed after redirect.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  useEffect(() => {
    let cancelled = false;
    loadGoogleScripts()
      .then(async () => {
        if (cancelled) return;
        setScriptsLoaded(true);
        const session = await restoreSession();
        if (session && !cancelled) {
          setToken(session.token);
          setProfile(session.profile ?? getStoredProfile());
          await syncScopeFlags(session.token);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Failed to load Google APIs"
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [syncScopeFlags]);

  const runConnect = useCallback(
    (scope: string, prompt?: "consent") => {
      if (!GOOGLE_CONFIG.clientId) {
        setError("Missing Google client ID.");
        return;
      }
      setIsLoading(true);
      setError(null);
      startGoogleOAuthRedirect({
        scope,
        prompt,
        returnTo: `${window.location.pathname}${window.location.search}`,
      });
    },
    []
  );

  const connectDrive = useCallback(() => {
    runConnect(GOOGLE_DRIVE_SCOPES);
  }, [runConnect]);

  const connectGmail = useCallback(() => {
    const opts = gmailConnectOptions(!!token);
    runConnect(opts.scope, opts.prompt);
  }, [runConnect, token]);

  const connect = connectDrive;

  const disconnect = useCallback(async () => {
    await revokeAndClear(token);
    setToken(null);
    setProfile(null);
    setDriveConnected(false);
    setGmailConnected(false);
    setError(null);
    invalidateGoogleQueries();
  }, [token, invalidateGoogleQueries]);

  const refreshToken = useCallback(async () => {
    const stored = getStoredToken();
    if (!stored) {
      handleAuthFailure();
      return false;
    }
    const valid = await validateToken(stored);
    if (!valid) {
      await revokeAndClear(stored);
      handleAuthFailure();
      return false;
    }
    await applySession(stored, getStoredProfile());
    return true;
  }, [applySession, handleAuthFailure]);

  const connection: GoogleConnection = useMemo(
    () => ({
      connected: !!token,
      driveConnected,
      gmailConnected,
      token,
      profile,
      isLoading,
      error,
      scriptsLoaded,
    }),
    [token, driveConnected, gmailConnected, profile, isLoading, error, scriptsLoaded]
  );

  const value = useMemo(
    () => ({
      connection,
      connectDrive,
      connectGmail,
      connect,
      disconnect,
      refreshToken,
      applySession,
      handleAuthFailure,
      handleGmailAuthFailure,
      config: GOOGLE_CONFIG,
    }),
    [
      connection,
      connectDrive,
      connectGmail,
      connect,
      disconnect,
      refreshToken,
      applySession,
      handleAuthFailure,
      handleGmailAuthFailure,
    ]
  );

  return (
    <GoogleConnectionContext.Provider value={value}>
      {children}
    </GoogleConnectionContext.Provider>
  );
}

/** @deprecated Use GoogleConnectionProvider */
export const GoogleDriveConnectionProvider = GoogleConnectionProvider;

export function useGoogleConnection() {
  const ctx = useContext(GoogleConnectionContext);
  if (!ctx) {
    throw new Error(
      "useGoogleConnection must be used within GoogleConnectionProvider"
    );
  }
  return ctx;
}

/** @deprecated Use useGoogleConnection */
export const useGoogleDriveConnection = useGoogleConnection;

export function isGoogleAuthError(error: unknown): error is GoogleAuthError {
  return error instanceof GoogleAuthError;
}

/** @deprecated Use isGoogleAuthError */
export function isDriveAuthError(error: unknown): error is GoogleAuthError {
  return isGoogleAuthError(error);
}
